import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Trash2, Plus, Download, Upload, FileText, Pencil, X } from 'lucide-react';
import { CreateOpportunityModal } from '@/components/CreateOpportunityModal';
import { ContactFormModal } from '@/components/ContactFormModal';
import { OpportunityDetailView, OpportunityScoreCard } from '@/components/OpportunityDetailView';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dropzone } from '@/components/ui/Dropzone';
import { Banner } from '@/components/ui/Banner';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  StatusBadge,
  PotentialBadge,
  PipelineStageBadge,
  Badge,
} from '@/components/ui/StatusBadge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Input, Label, Select, Textarea } from '@/components/ui/Input';
import { Tabs } from '@/components/ui/Tabs';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import {
  prospectsApi,
  type Contact,
  type Establishment,
  type ProspectDetail,
} from '@/services/prospects.api';
import {
  opportunitiesApi,
  ORIGIN_VISUAL,
  ORIGIN_ORDER,
  type OpportunityListItem,
  type OpportunityOrigin,
} from '@/services/opportunities.api';
import {
  documentsApi,
  type DocumentCategory,
  type DocumentItem,
  DOCUMENT_CATEGORY_LABELS,
  DOCUMENT_CATEGORY_TONES,
} from '@/services/documents.api';
import { TimelineSection } from '@/components/TimelineSection';
import {
  sequencesApi,
  type ProspectSequenceEnrollment,
  type Sequence as SequenceSummary,
} from '@/services/sequences.api';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';
import { displayName } from '@shared/prospect';
import { legalFormShort, legalFormLong } from '@shared/legal-forms';
import { displayWebsite } from '@shared/website';

type Tab = 'info' | 'opportunities' | 'documents';

const TABS: readonly Tab[] = ['info', 'opportunities', 'documents'];

function parseTab(raw: string | null): Tab {
  return (TABS as readonly string[]).includes(raw ?? '') ? (raw as Tab) : 'info';
}

export function ProspectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const token = useAuthStore((s) => s.accessToken)!;
  const [prospect, setProspect] = useState<ProspectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Onglet + opportunité sélectionnée synchronisés avec l'URL :
  //   ?tab=opportunities&opportunity=<id>
  // Permet au bouton retour du navigateur, au refresh, et aux liens
  // partagés (deep-link depuis notifications) de fonctionner naturellement.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));
  const selectedOpportunityId = tab === 'opportunities' ? searchParams.get('opportunity') : null;

  const setTab = useCallback(
    (next: Tab) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === 'info') p.delete('tab');
          else p.set('tab', next);
          // Changer d'onglet ferme toujours le détail d'opportunité.
          p.delete('opportunity');
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const openOpportunity = useCallback(
    (oppId: string) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('tab', 'opportunities');
          p.set('opportunity', oppId);
          return p;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const closeOpportunity = useCallback(() => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('opportunity');
        return p;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  async function load() {
    if (!id) return;
    setLoading(true);
    try {
      const p = await prospectsApi.get(token, id);
      setProspect(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-4 w-1/2" />
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 space-y-4">
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
            <Skeleton className="h-64" />
          </div>
        </div>
      </AppLayout>
    );
  }
  if (error || !prospect) {
    return (
      <AppLayout>
        <Banner tone="danger">{error ?? 'Prospect introuvable'}</Banner>
      </AppLayout>
    );
  }

  const prospectDisplayName = displayName(prospect);
  const subtitleParts: string[] = [];
  if (prospect.type === 'company') {
    if (prospect.siret) subtitleParts.push(`SIRET ${prospect.siret}`);
    if (prospect.siren) subtitleParts.push(`SIREN ${prospect.siren}`);
  } else {
    if (prospect.birthDate) {
      const d = new Date(prospect.birthDate).toLocaleDateString('fr-FR');
      subtitleParts.push(`Né${prospect.civility === 'Mme' ? 'e' : ''} le ${d}`);
    }
    if (prospect.isPPE) subtitleParts.push('PPE');
  }
  if (prospect.addressCity) subtitleParts.push(prospect.addressCity);

  return (
    <AppLayout>
      <PageHeader
        title={prospectDisplayName}
        subtitle={subtitleParts.join(' · ')}
        /* Libellé d'activité principale (fabrique.social.gouv.fr). Affiché
           sous SIRET/SIREN sur les prospects entreprise uniquement. */
        extra={prospect.type === 'company' && prospect.nafLabel ? prospect.nafLabel : undefined}
        breadcrumbs={[{ label: 'Prospects', to: '/prospects' }, { label: prospectDisplayName }]}
        badges={
          <>
            <StatusBadge status={prospect.status} />
            <PotentialBadge potential={prospect.potential} />
            {prospect.isPPE && <Badge tone="warning">PPE</Badge>}
          </>
        }
        /* Bouton « Supprimer le prospect » retiré : un prospect ne peut plus
           être supprimé depuis l'UI. Le soft-delete backend subsiste pour les
           opérations admin directes / anonymisation RGPD. */
      />

      <Tabs
        tabs={[
          { key: 'info', label: 'Infos' },
          { key: 'opportunities', label: 'Opportunités' },
          { key: 'documents', label: 'Documents' },
        ]}
        current={tab}
        onChange={(k) => setTab(k)}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {tab === 'info' && <InfoTab prospect={prospect} onReload={load} />}
          {tab === 'opportunities' &&
            (selectedOpportunityId ? (
              <OpportunityDetailView
                opportunityId={selectedOpportunityId}
                onBack={closeOpportunity}
                hideTimeline
              />
            ) : (
              <OpportunitiesTab prospect={prospect} onOpen={openOpportunity} />
            ))}
          {tab === 'documents' && <DocumentsTab prospectId={prospect.id} />}
        </div>

        <aside className="space-y-6 lg:col-span-1">
          {/* Séquences : rendu désactivé pour le moment. Conservées en commentaire
              JSX pour réactiver facilement - <SequencesSection prospectId={prospect.id} /> */}
          {selectedOpportunityId && <OpportunityScoreCard opportunityId={selectedOpportunityId} />}
          <TimelineSection
            prospectId={prospect.id}
            opportunityId={selectedOpportunityId ?? undefined}
            compact
          />
        </aside>
      </div>
    </AppLayout>
  );
}

function OpportunitiesTab({
  prospect,
  onOpen,
}: {
  prospect: ProspectDetail;
  onOpen: (oppId: string) => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const [items, setItems] = useState<OpportunityListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const res = await opportunitiesApi.list(token, { prospect_id: prospect.id, limit: 50 });
      setItems(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }, [prospect.id, token]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <Card>
        <Banner tone="danger">{error}</Banner>
      </Card>
    );
  }
  if (items === null) {
    return (
      <Card>
        <Skeleton className="h-24 w-full" />
      </Card>
    );
  }

  return (
    <>
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <CardTitle>Opportunités{items.length > 0 ? ` (${items.length})` : ''}</CardTitle>
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => setCreateOpen(true)}
          >
            Nouvelle opportunité
          </Button>
        </div>
        {items.length === 0 ? (
          <EmptyState
            title="Aucune opportunité"
            description="Créez la première opportunité commerciale pour ce prospect."
          />
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  onClick={() => onOpen(o.id)}
                  className="flex w-full items-center justify-between gap-4 py-3 text-left text-sm hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-text-muted">{o.reference}</span>
                      <PipelineStageBadge stage={o.stage} />
                    </div>
                    {/* Ligne 2 : type de contrat agrandi (text-sm + medium
                        + couleur brand au lieu de text-xs muted dilué). */}
                    <div className="mt-1 text-sm font-medium text-brand">
                      {o.contractType.label}
                    </div>
                    {/* Ligne 3 : commission en noir gras à gauche, commercial
                        assigné en gris à droite. La commission est l'enjeu
                        financier brut le plus pertinent à voir au survol de
                        la liste. */}
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-text-muted">
                      <span className="text-sm font-bold text-brand">
                        {o.commissionAmount
                          ? `${Number(o.commissionAmount).toLocaleString('fr-FR')} €`
                          : '-'}
                      </span>
                      <span aria-hidden="true">·</span>
                      <span>
                        {o.assignedUser.firstName} {o.assignedUser.lastName}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] text-text-muted">
                      Date d&apos;effet {new Date(o.effectiveDate).toLocaleDateString('fr-FR')}
                    </div>
                    {/* Prime TTC retirée de l'UI 2026-04-27. Affichage de la
                        Prime HT à la place (champ effectivement renseigné par
                        le client). */}
                    <div className="mt-0.5 text-sm font-medium text-brand">
                      Prime HT{' '}
                      {o.premiumHt ? `${Number(o.premiumHt).toLocaleString('fr-FR')} €` : '-'}
                    </div>
                    <div className="text-[11px] text-text-muted">
                      Score {o.effectiveScore ? Math.round(Number(o.effectiveScore)) : '-'}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {createOpen && (
        <CreateOpportunityModal
          initialProspect={{
            id: prospect.id,
            type: prospect.type,
            displayName: displayName(prospect),
            siret: prospect.siret,
          }}
          onClose={() => setCreateOpen(false)}
          onCreated={(opp) => {
            setCreateOpen(false);
            toast({
              tone: 'success',
              title: 'Opportunité créée',
              message: opp.reference,
            });
            // Ouvre directement la nouvelle opp dans l'onglet - la timeline
            // latérale se recale sur l'opp automatiquement.
            onOpen(opp.id);
          }}
        />
      )}
    </>
  );
}

function DocumentsTab({ prospectId }: { prospectId: string }) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const confirm = useConfirm();
  const [documents, setDocuments] = useState<DocumentItem[] | null>(null);
  const [opportunities, setOpportunities] = useState<OpportunityListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [oppFilter, setOppFilter] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [draftCategory, setDraftCategory] = useState<DocumentCategory>('kbis');
  const [draftOpp, setDraftOpp] = useState<string>('');
  // Fichiers déposés en attente d'upload, avec catégorie éditable PAR fichier
  // (défaut = la catégorie courante du sélecteur au moment du dépôt).
  const [pending, setPending] = useState<Array<{ file: File; category: DocumentCategory }>>([]);

  async function load() {
    try {
      const [docs, opps] = await Promise.all([
        documentsApi.list(token, { prospect_id: prospectId, limit: 100 }),
        opportunitiesApi.list(token, { prospect_id: prospectId, limit: 50 }),
      ]);
      setDocuments(docs.data);
      setOpportunities(opps.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospectId]);

  function addPending(files: File[]) {
    setPending((prev) => [...prev, ...files.map((file) => ({ file, category: draftCategory }))]);
  }

  async function handleUploadPending() {
    if (pending.length === 0) return;
    setUploading(true);
    const failures: string[] = [];
    let ok = 0;
    // Upload séquentiel : 1 requête par fichier (l'endpoint accepte 1 fichier),
    // erreur isolée par fichier (un rejet ne bloque pas les autres).
    for (const item of pending) {
      try {
        await documentsApi.upload(token, item.file, {
          prospectId,
          opportunityId: draftOpp || undefined,
          category: item.category,
        });
        ok++;
      } catch (err) {
        failures.push(`${item.file.name} : ${err instanceof Error ? err.message : 'erreur'}`);
      }
    }
    setUploading(false);
    setPending([]);
    await load();
    if (failures.length === 0) {
      toast({ tone: 'success', title: 'Upload terminé', message: `${ok} document(s) uploadé(s)` });
    } else {
      toast({
        tone: 'danger',
        title: `${failures.length} échec(s)${ok ? ` · ${ok} OK` : ''}`,
        message: failures.join(' · '),
      });
    }
  }

  async function handleDownload(doc: DocumentItem) {
    try {
      const res = await documentsApi.downloadUrl(token, doc.id);
      // URL présignée portée par query param : l'ouvrir dans un nouvel onglet
      // déclenche le téléchargement (content-disposition: attachment).
      window.open(res.url, '_blank', 'noopener');
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Téléchargement impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  async function handleDelete(doc: DocumentItem) {
    const ok = await confirm({
      title: `Supprimer « ${doc.fileName} » ?`,
      variant: 'danger',
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    try {
      await documentsApi.delete(token, doc.id);
      toast({ tone: 'success', message: 'Document supprimé' });
      await load();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Suppression impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  if (error) {
    return (
      <Card>
        <Banner tone="danger">{error}</Banner>
      </Card>
    );
  }
  if (documents === null) {
    return (
      <Card>
        <Skeleton className="h-24 w-full" />
      </Card>
    );
  }

  const filtered = oppFilter ? documents.filter((d) => d.opportunityId === oppFilter) : documents;

  return (
    <Card>
      <div className="mb-4 space-y-3 border-b border-border-subtle pb-4">
        <div className="flex items-center justify-between">
          <CardTitle>Documents ({documents.length})</CardTitle>
        </div>
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="doc-category">Catégorie par défaut</Label>
              <Select
                id="doc-category"
                value={draftCategory}
                onChange={(e) => setDraftCategory(e.target.value as DocumentCategory)}
              >
                {(Object.keys(DOCUMENT_CATEGORY_LABELS) as DocumentCategory[]).map((c) => (
                  <option key={c} value={c}>
                    {DOCUMENT_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex-1">
              <Label htmlFor="doc-opp">Opportunité (optionnel)</Label>
              <Select id="doc-opp" value={draftOpp} onChange={(e) => setDraftOpp(e.target.value)}>
                <option value="">-</option>
                {opportunities.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.reference} · {o.contractType.code}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <Dropzone onFiles={addPending} disabled={uploading} />

          {pending.length > 0 && (
            <div className="space-y-2 rounded-md border border-border-subtle bg-gray-50 p-3">
              {pending.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <FileText className="h-4 w-4 shrink-0 text-text-muted" />
                  <span className="min-w-0 flex-1 truncate text-brand">{item.file.name}</span>
                  <span className="shrink-0 text-xs text-text-muted">
                    {(item.file.size / 1024).toFixed(0)} Ko
                  </span>
                  <Select
                    aria-label="Catégorie"
                    value={item.category}
                    onChange={(e) =>
                      setPending((prev) =>
                        prev.map((p, i) =>
                          i === idx ? { ...p, category: e.target.value as DocumentCategory } : p,
                        ),
                      )
                    }
                    className="w-36 shrink-0"
                  >
                    {(Object.keys(DOCUMENT_CATEGORY_LABELS) as DocumentCategory[]).map((c) => (
                      <option key={c} value={c}>
                        {DOCUMENT_CATEGORY_LABELS[c]}
                      </option>
                    ))}
                  </Select>
                  <button
                    type="button"
                    onClick={() => setPending((prev) => prev.filter((_, i) => i !== idx))}
                    className="shrink-0 text-text-muted transition-colors hover:text-danger"
                    aria-label="Retirer"
                    disabled={uploading}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <div className="flex justify-end pt-1">
                <Button
                  variant="primary"
                  leadingIcon={<Upload className="h-4 w-4" />}
                  onClick={handleUploadPending}
                  loading={uploading}
                >
                  Uploader {pending.length} fichier{pending.length > 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {opportunities.length > 0 && (
        <div className="mb-3 flex items-center gap-2">
          <span className="text-xs text-text-muted">Filtrer :</span>
          <Select
            value={oppFilter}
            onChange={(e) => setOppFilter(e.target.value)}
            className="w-auto"
          >
            <option value="">Tous</option>
            {opportunities.map((o) => (
              <option key={o.id} value={o.id}>
                {o.reference}
              </option>
            ))}
          </Select>
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          title="Aucun document"
          description="Glissez vos fichiers (PDF, images, Word/Excel, mails Outlook) pour enrichir la fiche."
        />
      ) : (
        <ul className="divide-y divide-gray-100">
          {filtered.map((doc) => (
            <li key={doc.id} className="flex items-center gap-3 py-3 text-sm">
              <FileText className="h-5 w-5 shrink-0 text-text-muted" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Badge tone={DOCUMENT_CATEGORY_TONES[doc.category]}>
                    {DOCUMENT_CATEGORY_LABELS[doc.category]}
                  </Badge>
                  <span className="truncate font-medium text-brand">{doc.fileName}</span>
                </div>
                <div className="mt-0.5 text-xs text-text-muted">
                  {new Date(doc.createdAt).toLocaleDateString('fr-FR')} · {doc.uploader.firstName}{' '}
                  {doc.uploader.lastName}
                  {doc.opportunity && ` · ${doc.opportunity.reference}`}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<Download className="h-3.5 w-3.5" />}
                onClick={() => handleDownload(doc)}
              >
                Télécharger
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(doc)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function InfoTab({ prospect, onReload }: { prospect: ProspectDetail; onReload: () => void }) {
  if (prospect.type === 'individual') {
    return <IndividualCard prospect={prospect} onReload={onReload} />;
  }
  return (
    <>
      <EnterpriseCard prospect={prospect} onReload={onReload} />
      <ContactsSection prospect={prospect} onReload={onReload} />
      <EstablishmentsSection prospect={prospect} onReload={onReload} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Carte particulier - état civil + coordonnées personnelles + adresse + PPE
// ---------------------------------------------------------------------------
function IndividualCard({
  prospect,
  onReload,
}: {
  prospect: ProspectDetail;
  onReload: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  interface FormState {
    civility: 'M' | 'Mme';
    firstName: string;
    lastName: string;
    birthDate: string;
    personalEmail: string;
    personalPhone: string;
    isPPE: boolean;
    addressStreet: string;
    addressPostalCode: string;
    addressCity: string;
    observations: string;
    // Refonte 2026-05-23 : origin déplacé depuis Opportunity.
    origin: OpportunityOrigin | '';
  }

  function initial(src: ProspectDetail): FormState {
    return {
      civility: (src.civility ?? 'M') as 'M' | 'Mme',
      firstName: src.firstName ?? '',
      lastName: src.lastName ?? '',
      birthDate: src.birthDate ? src.birthDate.slice(0, 10) : '',
      personalEmail: src.personalEmail ?? '',
      personalPhone: src.personalPhone ?? '',
      isPPE: src.isPPE,
      addressStreet: src.addressStreet ?? '',
      addressPostalCode: src.addressPostalCode ?? '',
      addressCity: src.addressCity ?? '',
      observations: src.observations ?? '',
      origin: src.origin ?? '',
    };
  }

  const [form, setForm] = useState<FormState>(() => initial(prospect));

  function start() {
    setForm(initial(prospect));
    setEditing(true);
  }
  function cancel() {
    setForm(initial(prospect));
    setEditing(false);
  }

  async function save() {
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        civility: form.civility,
        firstName: form.firstName,
        lastName: form.lastName,
        birthDate: form.birthDate,
        isPPE: form.isPPE,
      };
      body.personalEmail = form.personalEmail || null;
      body.personalPhone = form.personalPhone || null;
      body.addressStreet = form.addressStreet || null;
      body.addressPostalCode = form.addressPostalCode || null;
      body.addressCity = form.addressCity || null;
      body.observations = form.observations || null;
      // Refonte 2026-05-23 : origin nullable côté backend ('' UI → null DB).
      body.origin = form.origin === '' ? null : form.origin;

      await prospectsApi.update(token, prospect.id, body);
      toast({ tone: 'success', message: 'Informations mises à jour.' });
      setEditing(false);
      onReload();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Mise à jour impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <Card>
        <div className="flex items-start justify-between gap-3">
          <CardTitle>Identité</CardTitle>
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<Pencil className="h-3.5 w-3.5" />}
            onClick={start}
          >
            Modifier
          </Button>
        </div>
        <dl className="mt-4 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <Field label="Civilité" value={prospect.civility === 'Mme' ? 'Mme' : 'M.'} />
          <Field label="Prénom" value={prospect.firstName ?? '-'} />
          <Field label="Nom" value={prospect.lastName ?? '-'} />
          <Field
            label="Date de naissance"
            value={
              prospect.birthDate ? new Date(prospect.birthDate).toLocaleDateString('fr-FR') : '-'
            }
          />
          <Field label="Email" value={prospect.personalEmail ?? '-'} />
          <Field label="Téléphone" value={prospect.personalPhone ?? '-'} />
          <Field
            label="Adresse"
            value={
              [prospect.addressStreet, prospect.addressPostalCode, prospect.addressCity]
                .filter(Boolean)
                .join(' · ') || '-'
            }
            full
          />
          <Field
            label="PPE (LCB-FT)"
            value={prospect.isPPE ? 'Oui - Personne Politiquement Exposée' : 'Non'}
            full
          />
          {/* Refonte 2026-05-23 : Origine (canal d'acquisition) déplacé
              depuis Opportunity vers Prospect. */}
          <Field
            label="Origine"
            value={
              prospect.origin ? (
                <span
                  className={
                    'inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ' +
                    ORIGIN_VISUAL[prospect.origin].badge
                  }
                >
                  {ORIGIN_VISUAL[prospect.origin].label}
                </span>
              ) : (
                '-'
              )
            }
            full
          />
          <Field label="Observations" value={prospect.observations ?? '-'} full />
        </dl>
      </Card>
    );
  }

  return (
    <Card>
      <CardTitle>Identité</CardTitle>
      <div className="mt-4 space-y-4">
        <div className="grid grid-cols-[120px_1fr_1fr] gap-3">
          <div>
            <Label>Civilité</Label>
            <Select
              value={form.civility}
              onChange={(e) => setForm({ ...form, civility: e.target.value as 'M' | 'Mme' })}
            >
              <option value="M">M.</option>
              <option value="Mme">Mme</option>
            </Select>
          </div>
          <div>
            <Label>Prénom</Label>
            <Input
              value={form.firstName}
              onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            />
          </div>
          <div>
            <Label>Nom</Label>
            <Input
              value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Date de naissance</Label>
            <Input
              type="date"
              value={form.birthDate}
              onChange={(e) => setForm({ ...form, birthDate: e.target.value })}
              max={new Date().toISOString().slice(0, 10)}
            />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-brand">
              <input
                type="checkbox"
                checked={form.isPPE}
                onChange={(e) => setForm({ ...form, isPPE: e.target.checked })}
                className="h-4 w-4 rounded border-border text-accent focus:ring-accent/30"
              />
              <span>
                PPE
                <span className="ml-1 text-xs text-text-muted">- LCB-FT</span>
              </span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={form.personalEmail}
              onChange={(e) => setForm({ ...form, personalEmail: e.target.value })}
            />
          </div>
          <div>
            <Label>Téléphone</Label>
            <Input
              type="tel"
              value={form.personalPhone}
              onChange={(e) => setForm({ ...form, personalPhone: e.target.value })}
            />
          </div>
        </div>
        <div>
          <Label>Adresse</Label>
          <Input
            placeholder="Rue"
            value={form.addressStreet}
            onChange={(e) => setForm({ ...form, addressStreet: e.target.value })}
          />
          <div className="mt-2 grid grid-cols-3 gap-2">
            <Input
              placeholder="CP"
              value={form.addressPostalCode}
              onChange={(e) => setForm({ ...form, addressPostalCode: e.target.value })}
            />
            <Input
              className="col-span-2"
              placeholder="Ville"
              value={form.addressCity}
              onChange={(e) => setForm({ ...form, addressCity: e.target.value })}
            />
          </div>
        </div>
        {/* Refonte 2026-05-23 : Origine (canal d'acquisition) éditable. */}
        <div>
          <Label htmlFor="indiv-origin">Origine</Label>
          <Select
            id="indiv-origin"
            value={form.origin}
            onChange={(e) => setForm({ ...form, origin: e.target.value as OpportunityOrigin | '' })}
          >
            <option value="">— Non renseignée —</option>
            {ORIGIN_ORDER.map((code) => (
              <option key={code} value={code}>
                {ORIGIN_VISUAL[code].label}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label>Observations</Label>
          <Textarea
            rows={3}
            value={form.observations}
            onChange={(e) => setForm({ ...form, observations: e.target.value })}
          />
        </div>
        <div className="flex justify-end gap-2 border-t border-border-subtle pt-4">
          <Button variant="secondary" onClick={cancel} disabled={saving}>
            Annuler
          </Button>
          <Button onClick={save} loading={saving} disabled={saving}>
            Enregistrer
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Field({ label, value, full }: { label: string; value: React.ReactNode; full?: boolean }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <dt className="text-xs font-medium uppercase tracking-wide text-text-muted">{label}</dt>
      <dd className="mt-1 text-sm text-brand">{value}</dd>
    </div>
  );
}

function EnterpriseCard({
  prospect,
  onReload,
}: {
  prospect: ProspectDetail;
  onReload: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  interface FormState {
    companyName: string;
    legalForm: string;
    nafCode: string;
    collectiveAgreement: string;
    employeeCount: string;
    creationDate: string; // YYYY-MM-DD
    phone: string;
    website: string;
    addressStreet: string;
    addressPostalCode: string;
    addressCity: string;
    observations: string;
    // Refonte 2026-05-23 : origin déplacé depuis Opportunity.
    origin: OpportunityOrigin | '';
  }

  function initial(src: ProspectDetail): FormState {
    return {
      companyName: src.companyName ?? '',
      legalForm: src.legalForm ?? '',
      nafCode: src.nafCode ?? '',
      collectiveAgreement: src.collectiveAgreement ?? '',
      employeeCount: src.employeeCount !== null ? String(src.employeeCount) : '',
      creationDate: src.creationDate ? src.creationDate.slice(0, 10) : '',
      phone: src.phone ?? '',
      website: src.website ?? '',
      addressStreet: src.addressStreet ?? '',
      addressPostalCode: src.addressPostalCode ?? '',
      addressCity: src.addressCity ?? '',
      observations: src.observations ?? '',
      origin: src.origin ?? '',
    };
  }

  const [form, setForm] = useState<FormState>(() => initial(prospect));

  function start() {
    setForm(initial(prospect));
    setEditing(true);
  }

  function cancel() {
    setForm(initial(prospect));
    setEditing(false);
  }

  async function save() {
    setSaving(true);
    try {
      // Payload partiel : on n'envoie que les champs renseignés. Les champs
      // vides côté UI → on envoie `undefined` (no-op) plutôt que `null`
      // (le schéma Zod n'accepte pas `null` sur ces champs). Pour « effacer »
      // une valeur, il faudrait étendre le schéma - hors scope ici.
      const body: Record<string, unknown> = {};
      // Raison sociale : requise (min 1 côté backend). On n'envoie que si
      // non vide → un champ vidé par mégarde ne tente pas un PATCH invalide.
      if (form.companyName.trim()) body.companyName = form.companyName.trim();
      if (form.legalForm) body.legalForm = form.legalForm;
      if (form.nafCode) body.nafCode = form.nafCode;
      if (form.collectiveAgreement) body.collectiveAgreement = form.collectiveAgreement;
      if (form.employeeCount !== '') {
        const n = Number(form.employeeCount);
        if (Number.isFinite(n) && n >= 0) body.employeeCount = Math.floor(n);
      }
      if (form.creationDate) {
        // Backend attend ISO datetime → on convertit la date locale en
        // midnight UTC. Cohérent avec le reste de l'app (cf comments dans
        // kpi.api.ts `presetPeriod`).
        body.creationDate = new Date(`${form.creationDate}T00:00:00Z`).toISOString();
      }
      if (form.website) body.website = form.website;
      if (form.addressStreet) body.addressStreet = form.addressStreet;
      if (form.addressPostalCode) body.addressPostalCode = form.addressPostalCode;
      if (form.addressCity) body.addressCity = form.addressCity;
      if (form.observations) body.observations = form.observations;
      // Téléphone entreprise : nullable côté schéma → effaçable (vide → null).
      body.phone = form.phone.trim() === '' ? null : form.phone.trim();
      // Refonte 2026-05-23 : origin nullable ('' UI → null DB, effacement explicite).
      body.origin = form.origin === '' ? null : form.origin;

      await prospectsApi.update(token, prospect.id, body);
      toast({ tone: 'success', message: 'Infos entreprise mises à jour.' });
      setEditing(false);
      onReload();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Mise à jour impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <CardTitle>Entreprise</CardTitle>
        {!editing && (
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<Pencil className="h-3.5 w-3.5" />}
            onClick={start}
          >
            Modifier
          </Button>
        )}
      </div>

      {!editing ? (
        <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-3">
          {/* Forme juridique : affichage du label court dérivé du code INSEE
              stocké en BDD (ex: "5499" → "SARL"). Tooltip natif HTML pour le
              libellé long. Si le code est inconnu de la table, `legalFormShort`
              renvoie le code brut en fallback transparent. */}
          <Info
            label="Forme juridique"
            value={
              prospect.legalForm ? (
                <span title={legalFormLong(prospect.legalForm) ?? undefined}>
                  {legalFormShort(prospect.legalForm)}
                </span>
              ) : null
            }
          />
          <Info label="Code NAF" value={prospect.nafCode} />
          <Info label="Téléphone" value={prospect.phone} />
          <Info label="Convention collective" value={prospect.collectiveAgreement} />
          <Info label="Effectif" value={prospect.employeeCount} />
          {/* Date au format français jj/mm/aaaa (au lieu de l'ISO tronqué). */}
          <Info
            label="Création"
            value={
              prospect.creationDate
                ? new Date(prospect.creationDate).toLocaleDateString('fr-FR')
                : null
            }
          />
          {/* Site web : affichage compact sans protocole + lien cliquable
              nouvel onglet. Le backend stocke l'URL complète (https://...)
              pour que le href fonctionne correctement ; seul l'affichage
              est tronqué pour la lisibilité. */}
          <Info
            label="Site web"
            value={
              prospect.website ? (
                <a
                  href={prospect.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  {displayWebsite(prospect.website)}
                </a>
              ) : null
            }
          />
          {/* Origine (canal d'acquisition) — placé juste après Site web
              (3ᵉ ligne, 2ᵉ position). Cellule simple (pas de span). */}
          <Info
            label="Origine"
            value={
              prospect.origin ? (
                <span
                  className={
                    'inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ' +
                    ORIGIN_VISUAL[prospect.origin].badge
                  }
                >
                  {ORIGIN_VISUAL[prospect.origin].label}
                </span>
              ) : null
            }
          />
          <Info
            label="Adresse"
            span={3}
            value={
              [
                prospect.addressStreet,
                [prospect.addressPostalCode, prospect.addressCity].filter(Boolean).join(' '),
              ]
                .filter(Boolean)
                .join(' · ') || null
            }
          />
          <Info label="Observations" value={prospect.observations} span={3} multiline />
        </dl>
      ) : (
        <div className="mt-4 space-y-4">
          <div>
            <Label htmlFor="ent-companyName">Raison sociale</Label>
            <Input
              id="ent-companyName"
              value={form.companyName}
              onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="ent-legalForm">Forme juridique</Label>
              <Input
                id="ent-legalForm"
                value={form.legalForm}
                onChange={(e) => setForm({ ...form, legalForm: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="ent-nafCode">Code NAF</Label>
              <Input
                id="ent-nafCode"
                value={form.nafCode}
                onChange={(e) => setForm({ ...form, nafCode: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="ent-collective">Convention collective</Label>
              <Input
                id="ent-collective"
                value={form.collectiveAgreement}
                onChange={(e) => setForm({ ...form, collectiveAgreement: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="ent-effectif">Effectif</Label>
              <Input
                id="ent-effectif"
                type="number"
                min="0"
                step="1"
                value={form.employeeCount}
                onChange={(e) => setForm({ ...form, employeeCount: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="ent-creation">Création</Label>
              <Input
                id="ent-creation"
                type="date"
                value={form.creationDate}
                onChange={(e) => setForm({ ...form, creationDate: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="ent-phone">Téléphone</Label>
              <Input
                id="ent-phone"
                type="tel"
                placeholder="01 23 45 67 89"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="ent-website">Site web</Label>
              <Input
                id="ent-website"
                // `type="text"` (et non "url") : la validation browser est
                // trop stricte et rejette "www.example.com". La normalisation
                // (ajout du https:// implicite) est faite côté backend Zod
                // via `normalizeWebsite`. Le placeholder guide l'UX.
                type="text"
                placeholder="www.exemple.com"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="ent-street">Adresse</Label>
            <Input
              id="ent-street"
              placeholder="Rue"
              value={form.addressStreet}
              onChange={(e) => setForm({ ...form, addressStreet: e.target.value })}
            />
            <div className="mt-2 grid grid-cols-3 gap-2">
              <Input
                placeholder="CP"
                value={form.addressPostalCode}
                onChange={(e) => setForm({ ...form, addressPostalCode: e.target.value })}
              />
              <Input
                className="col-span-2"
                placeholder="Ville"
                value={form.addressCity}
                onChange={(e) => setForm({ ...form, addressCity: e.target.value })}
              />
            </div>
          </div>
          {/* Refonte 2026-05-23 : Origine (canal d'acquisition) éditable. */}
          <div>
            <Label htmlFor="ent-origin">Origine</Label>
            <Select
              id="ent-origin"
              value={form.origin}
              onChange={(e) =>
                setForm({ ...form, origin: e.target.value as OpportunityOrigin | '' })
              }
            >
              <option value="">— Non renseignée —</option>
              {ORIGIN_ORDER.map((code) => (
                <option key={code} value={code}>
                  {ORIGIN_VISUAL[code].label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="ent-observations">Observations</Label>
            <Textarea
              id="ent-observations"
              rows={4}
              value={form.observations}
              onChange={(e) => setForm({ ...form, observations: e.target.value })}
            />
          </div>
          <div className="flex justify-end gap-2 border-t border-border-subtle pt-4">
            <Button variant="secondary" size="sm" onClick={cancel} disabled={saving}>
              Annuler
            </Button>
            <Button variant="primary" size="sm" onClick={save} loading={saving}>
              Enregistrer
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function Info({
  label,
  value,
  span,
  multiline,
}: {
  label: string;
  /** Accepte string/number pour l'usage courant, ou ReactNode pour un
   *  rendu enrichi (ex: `<span title="...">SARL</span>`). `null`/`undefined`
   *  /`''` affichent le fallback "-". */
  value: React.ReactNode;
  span?: 1 | 2 | 3;
  multiline?: boolean;
}) {
  const spanCls = span === 3 ? 'sm:col-span-3' : span === 2 ? 'sm:col-span-2' : '';
  return (
    <div className={spanCls}>
      <dt className="text-[11px] font-medium uppercase tracking-wider text-text-muted">{label}</dt>
      <dd className={cn('mt-1 text-text-primary', multiline && 'whitespace-pre-wrap')}>
        {value === null || value === undefined || value === '' ? (
          <span className="text-gray-400">-</span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

// ---- Contacts -------------------------------------------------------------

const CONTACT_ROLE_LABELS: Record<Contact['role'], string> = {
  decision_maker: 'Décideur',
  prescriber: 'Prescripteur',
  manager: 'Gestionnaire',
  accountant: 'Comptable',
  other: 'Autre',
};

function ContactsSection({
  prospect,
  onReload,
}: {
  prospect: ProspectDetail;
  onReload: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const confirm = useConfirm();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);

  // Liste des emails déjà utilisés → alimente la garde UX non bloquante de la
  // modale contact. Un email dupliqué sur le même prospect déclenche un
  // warning mais ne bloque pas la création (un email générique
  // `contact@societe.fr` peut légitimement apparaître sur 2 contacts).
  const existingEmails = prospect.contacts.map((c) => c.email).filter((e): e is string => !!e);

  // Détacher (point 4) : retire le rattachement. Si c'était le dernier, le
  // backend supprime le contact orphelin — on prévient l'utilisateur.
  async function detach(c: Contact) {
    const lastLink = c.linkedProspectsCount <= 1;
    const ok = await confirm({
      title: 'Détacher ce contact ?',
      message: lastLink
        ? "Ce contact n'est rattaché qu'à ce prospect : il sera définitivement supprimé."
        : 'Le contact restera rattaché aux autres entreprises.',
      variant: 'danger',
      confirmLabel: 'Détacher',
    });
    if (!ok) return;
    try {
      await prospectsApi.contacts.detach(token, prospect.id, c.id);
      onReload();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <CardTitle>Contacts</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<Plus className="h-3.5 w-3.5" />}
          onClick={() => setAdding(true)}
        >
          Ajouter
        </Button>
      </div>

      {prospect.contacts.length === 0 ? (
        <EmptyState
          title="Aucun contact"
          description="Ajoutez un décideur ou un gestionnaire pour suivre vos échanges."
          action={
            <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
              Ajouter un contact
            </Button>
          }
        />
      ) : (
        <ul className="divide-y divide-gray-100">
          {prospect.contacts.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-2 py-3 text-sm">
              <div className="min-w-0">
                <div className="font-medium text-brand">
                  {c.firstName} {c.lastName}
                  {c.isPrimary && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-accent-50 px-2 py-0.5 text-[10px] font-semibold text-accent-600">
                      Principal
                    </span>
                  )}
                  <span className="ml-2 text-[11px] font-normal text-text-muted">
                    {CONTACT_ROLE_LABELS[c.role]}
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-text-muted">
                  {c.email ?? '-'}
                  {' · '}
                  {c.phoneMobile ?? c.phoneLandline ?? '-'}
                  {c.linkedProspectsCount > 1 && (
                    <span className="ml-2 text-[11px] text-text-muted">
                      · partagé avec {c.linkedProspectsCount - 1} autre
                      {c.linkedProspectsCount - 1 > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {c.observations && (
                  <p className="mt-1 whitespace-pre-line text-xs italic text-text-muted">
                    {c.observations}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Modifier ${c.firstName} ${c.lastName}`}
                  leadingIcon={<Pencil className="h-3.5 w-3.5" />}
                  onClick={() => setEditing(c)}
                >
                  Modifier
                </Button>
                <Button variant="ghost" size="sm" onClick={() => detach(c)}>
                  Détacher
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Ajout : nouveau contact OU rattachement d'un existant (combobox). */}
      {adding && (
        <ContactFormModal
          existingEmails={existingEmails}
          searchExisting={(q) => prospectsApi.contacts.search(token, q, prospect.id)}
          onAttachExisting={async (contact) => {
            await prospectsApi.contacts.attach(token, prospect.id, { contactId: contact.id });
            setAdding(false);
            onReload();
          }}
          onClose={() => setAdding(false)}
          onSubmit={async (draft) => {
            await prospectsApi.contacts.create(token, prospect.id, {
              firstName: draft.firstName,
              lastName: draft.lastName,
              role: draft.role,
              ...(draft.email ? { email: draft.email } : {}),
              ...(draft.phoneMobile ? { phoneMobile: draft.phoneMobile } : {}),
              ...(draft.phoneLandline ? { phoneLandline: draft.phoneLandline } : {}),
              isPrimary: draft.isPrimary,
              observations: draft.observations || null,
            });
            setAdding(false);
            onReload();
          }}
        />
      )}

      {/* Édition d'un contact rattaché (personne + rôle/principal local). */}
      {editing && (
        <ContactFormModal
          title="Modifier le contact"
          initial={{
            firstName: editing.firstName,
            lastName: editing.lastName,
            role: editing.role,
            email: editing.email ?? '',
            phoneMobile: editing.phoneMobile ?? '',
            phoneLandline: editing.phoneLandline ?? '',
            isPrimary: editing.isPrimary,
            observations: editing.observations ?? '',
          }}
          linkedProspectsCount={editing.linkedProspectsCount}
          existingEmails={existingEmails.filter((e) => e !== editing.email)}
          onClose={() => setEditing(null)}
          onSubmit={async (draft) => {
            // Champs vides omis (Zod email/phone n'accepte pas ''). Vider un
            // champ côté UI le laisse inchangé — limite V1 assumée.
            await prospectsApi.contacts.update(token, prospect.id, editing.id, {
              firstName: draft.firstName,
              lastName: draft.lastName,
              role: draft.role,
              ...(draft.email ? { email: draft.email } : {}),
              ...(draft.phoneMobile ? { phoneMobile: draft.phoneMobile } : {}),
              ...(draft.phoneLandline ? { phoneLandline: draft.phoneLandline } : {}),
              isPrimary: draft.isPrimary,
              // Observation : envoyée toujours (null pour effacer volontairement).
              observations: draft.observations || null,
            });
            setEditing(null);
            onReload();
          }}
        />
      )}
    </Card>
  );
}

// ---- Établissements -------------------------------------------------------

function EstablishmentsSection({
  prospect,
  onReload,
}: {
  prospect: ProspectDetail;
  onReload: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Partial<Establishment>>({
    siret: '',
    name: '',
    isHeadquarters: false,
  });
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    try {
      await prospectsApi.establishments.create(token, prospect.id, draft);
      setAdding(false);
      setDraft({ siret: '', name: '', isHeadquarters: false });
      onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <CardTitle>Établissements</CardTitle>
        {!adding && (
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => setAdding(true)}
          >
            Ajouter
          </Button>
        )}
      </div>

      {prospect.establishments.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {prospect.establishments.map((e) => (
            <li key={e.id} className="py-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium text-brand">{e.name ?? 'Sans nom'}</span>
                {e.isHeadquarters && (
                  <span className="inline-flex items-center rounded-full bg-success-50 px-2 py-0.5 text-[10px] font-semibold text-success-600">
                    Siège
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-xs text-text-muted">
                SIRET {e.siret}
                {e.addressCity ? ` · ${e.addressCity}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}

      {prospect.establishments.length === 0 && !adding && (
        <p className="text-xs text-text-muted">Aucun établissement enregistré.</p>
      )}

      {adding && (
        <div className="mt-4 space-y-3 rounded-md border border-dashed border-border-subtle bg-gray-50 p-4">
          <div>
            <Label htmlFor="estSiret">SIRET</Label>
            <Input
              id="estSiret"
              value={draft.siret ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, siret: e.target.value.replace(/\D/g, '').slice(0, 14) })
              }
              inputMode="numeric"
              maxLength={14}
              className="font-mono"
            />
          </div>
          <div>
            <Label htmlFor="estName">Nom (optionnel)</Label>
            <Input
              id="estName"
              value={draft.name ?? ''}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-text-primary">
            <input
              type="checkbox"
              checked={draft.isHeadquarters ?? false}
              onChange={(e) => setDraft({ ...draft, isHeadquarters: e.target.checked })}
              className="accent-accent"
            />
            Siège social
          </label>
          {error && <Banner tone="danger">{error}</Banner>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
              Annuler
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={save}
              disabled={!(draft.siret && /^\d{14}$/.test(draft.siret))}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ===========================================================================
// Séquences - carte latérale (fiche prospect)
// ===========================================================================

// Exporté (jamais consommé) pour éviter l'erreur TS6133 sans supprimer le
// code - la carte est conservée pour une réactivation future. Voir le commentaire
// à l'intérieur de `ProspectDetailPage` (aside) pour la remettre en service.
export function SequencesSection({ prospectId }: { prospectId: string }) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const confirm = useConfirm();
  const [enrollments, setEnrollments] = useState<ProspectSequenceEnrollment[] | null>(null);
  const [sequences, setSequences] = useState<SequenceSummary[]>([]);
  const [chosenSeq, setChosenSeq] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    try {
      const [list, seqs] = await Promise.all([
        sequencesApi.listForProspect(token, prospectId),
        sequencesApi.list(token),
      ]);
      setEnrollments(list);
      setSequences(seqs.filter((s) => s.isActive));
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospectId]);

  async function enroll() {
    if (!chosenSeq) return;
    setSubmitting(true);
    try {
      await sequencesApi.enroll(token, chosenSeq, prospectId);
      toast({ tone: 'success', message: 'Prospect inscrit dans la séquence' });
      setChosenSeq('');
      await load();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    } finally {
      setSubmitting(false);
    }
  }

  async function cancel(sequenceId: string) {
    const ok = await confirm({
      title: 'Annuler cette séquence ?',
      message: 'Les tâches futures encore planifiées seront supprimées.',
      variant: 'warning',
      confirmLabel: 'Annuler la séquence',
      cancelLabel: 'Retour',
    });
    if (!ok) return;
    try {
      await sequencesApi.cancel(token, sequenceId, prospectId);
      toast({ tone: 'warning', message: 'Séquence annulée' });
      await load();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  // Filtre les séquences déjà historiquement utilisées (active/completed/cancelled)
  // - la contrainte unique (prospect_id, sequence_id) empêche la ré-inscription.
  const usedSequenceIds = new Set((enrollments ?? []).map((e) => e.sequenceId));
  const availableSequences = sequences.filter((s) => !usedSequenceIds.has(s.id));

  return (
    <Card>
      <CardTitle>Séquences</CardTitle>
      <div className="mt-3 space-y-2">
        {enrollments === null && <div className="text-xs text-text-muted">Chargement…</div>}
        {enrollments && enrollments.length === 0 && (
          <div className="text-xs text-text-muted">Aucune séquence pour ce prospect.</div>
        )}
        {(enrollments ?? []).map((e) => (
          <div
            key={e.id}
            className="flex items-center justify-between rounded-md border border-border-subtle bg-white p-2 text-sm"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-brand">{e.sequence.name}</div>
              <div className="mt-0.5 flex gap-2 text-xs text-text-muted">
                <EnrollmentBadge status={e.status} />
                {e.status === 'active' && <span>Étape {e.currentStep}</span>}
              </div>
            </div>
            {e.status === 'active' && (
              <Button variant="ghost" size="sm" onClick={() => void cancel(e.sequenceId)}>
                Annuler
              </Button>
            )}
          </div>
        ))}
      </div>
      {availableSequences.length > 0 && (
        <div className="mt-4 space-y-2 border-t border-border-subtle pt-4">
          <Label htmlFor="enroll-seq">Inscrire dans une séquence</Label>
          <Select id="enroll-seq" value={chosenSeq} onChange={(e) => setChosenSeq(e.target.value)}>
            <option value="">-</option>
            {availableSequences.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
          <Button
            variant="primary"
            size="sm"
            className="w-full"
            disabled={!chosenSeq}
            loading={submitting}
            onClick={() => void enroll()}
          >
            Inscrire
          </Button>
        </div>
      )}
    </Card>
  );
}

function EnrollmentBadge({ status }: { status: ProspectSequenceEnrollment['status'] }) {
  if (status === 'active') return <Badge tone="accent">Active</Badge>;
  if (status === 'completed') return <Badge tone="success">Terminée</Badge>;
  return <Badge tone="neutral">Annulée</Badge>;
}
